import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { showMutationError } from "utils/showMutationError";

/*
 * The Integrated Browser link, when Healthlink sent one back. Older responses are the bare
 * URL as a string rather than an object, and both shapes are still in the wild.
 */
export const readRegeneratedLink = (data) =>
  data?.link || (typeof data === "string" && data.includes("http") ? data : null);

export const useResendReferral = () => {
  const queryClient = useQueryClient();
  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useMutation({
    // This hook explains its own failures below; without this the global handler adds
    // "An error occurred. Please try again." on top of the reason.
    meta: { skipGlobalError: true },
    mutationFn: async ({ referralId }) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/healthlink/referrals/${referralId}/resend`
      );
      return response.data;
    },
    /*
     * The one place a resend is reported. Healthlink answers a specialist referral with a
     * fresh Integrated Browser link and an ordinary one with nothing, and the two mean
     * different things to the person who pressed the button — so the message is chosen
     * here rather than left to each caller, which is how the overview came to say its own
     * line on top of this one.
     */
    onSuccess: (data) => {
      toast.success(
        readRegeneratedLink(data)
          ? "Link regenerated. Please complete the form."
          : "Referral resent successfully."
      );
      queryClient.invalidateQueries({ queryKey: ["referralsList"] });
      queryClient.invalidateQueries({ queryKey: ["referralXml"] });
      queryClient.invalidateQueries({ queryKey: ["activeConsultationReferral"] });
      queryClient.invalidateQueries({ queryKey: ["activeConsultation"] });
    },
    /*
     * The backend's own sentence (KIN-1495). Every failure here used to be a 500 with a
     * body nothing read, so the user was told "something unexpected happened" for the
     * two cases that are neither unexpected nor the app's fault: a referral already
     * sent (409) and a practice with no address (409, "Add it in Practice Details").
     */
    onError: (error) =>
      showMutationError(
        error,
        "Something unexpected happened while resending the referral, please try again"
      ),
  });
};
